package com.vamvisions.main.pages.projects

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import com.vamvisions.main.components.AppStoreIcon
import com.vamvisions.main.components.Card
import com.vamvisions.main.components.ConnectCard
import com.vamvisions.main.components.ErrorView
import com.vamvisions.main.components.GitHubIcon
import com.vamvisions.main.components.GlobeIcon
import com.vamvisions.main.components.GooglePlayIcon
import com.vamvisions.main.components.LinkPill
import com.vamvisions.main.components.LoadingView
import com.vamvisions.main.components.PrivacyPolicyIcon
import com.vamvisions.main.components.Section
import com.vamvisions.main.components.SkillList
import com.vamvisions.main.components.StatusBadge
import com.vamvisions.main.core.presentation.i18n.resolve
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.di.koinViewModel
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.layout.PageChrome
import com.vamvisions.main.layout.PageLayout
import com.vamvisions.main.portfolio.domain.model.Store
import com.vamvisions.main.portfolio.presentation.projects.ProjectUi
import com.vamvisions.main.portfolio.presentation.projects.ProjectsAction
import com.vamvisions.main.portfolio.presentation.projects.ProjectsState
import com.vamvisions.main.portfolio.presentation.projects.ProjectsViewModel
import com.vamvisions.main.theme.rememberReveal
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** Intrinsic size of the project icons, so the layout doesn't shift while they load. */
private const val ICON_SIZE_PX = "180"

@Composable
fun ProjectsRoot(chrome: PageChrome) {
    val viewModel = koinViewModel<ProjectsViewModel>()
    val state by viewModel.state.collectAsState()
    LaunchedEffect(chrome.language) {
        viewModel.onAction(ProjectsAction.LanguageChanged(chrome.language))
    }
    PageLayout(title = LocalStrings.current.projectsTitle, chrome = chrome, panel = false) {
        ProjectsScreen(state = state, onAction = viewModel::onAction, onNavigate = chrome.onNavigate)
    }
}

/** The intro and the call-to-action card show in every state; the sections only once the content has loaded. */
@Composable
fun ProjectsScreen(
    state: ProjectsState,
    onAction: (ProjectsAction) -> Unit,
    onNavigate: (Route) -> Unit,
) {
    val strings = LocalStrings.current
    Div(attrs = { classes(ProjectsStyles.page) }) {
        Div(attrs = { classes(ProjectsStyles.intro) }) {
            H1(attrs = { classes(ProjectsStyles.introTitle) }) { Text(strings.projectsIntroTitle) }
            P(attrs = { classes(ProjectsStyles.introText) }) { Text(strings.projectsIntroText) }
        }
        val error = state.error
        when {
            error != null -> ErrorView(error.resolve(strings)) { onAction(ProjectsAction.Retry) }
            !state.hasContent && state.isLoading -> LoadingView()
            // While another language loads, the previous content stays and is marked busy.
            else -> Div(attrs = {
                classes(ProjectsStyles.content)
                attr("aria-busy", state.isLoading.toString())
            }) {
                if (state.featured.isNotEmpty()) {
                    Section("now", strings.projectsNowTitle, strings.projectsNowEyebrow) {
                        ProjectList(state.featured, featured = true, onNavigate)
                    }
                }
                if (state.earlier.isNotEmpty()) {
                    Section("earlier", strings.projectsEarlierTitle, strings.projectsEarlierEyebrow) {
                        ProjectList(state.earlier, featured = false, onNavigate)
                    }
                }
            }
        }
        ConnectCard(onNavigate, title = strings.projectsConnectTitle, text = strings.projectsConnectText)
    }
}

/** Featured projects one per row, earlier ones in a grid. Each card fades in the first time it scrolls into view. */
@Composable
private fun ProjectList(projects: List<ProjectUi>, featured: Boolean, onNavigate: (Route) -> Unit) {
    Ul(attrs = { classes(if (featured) ProjectsStyles.featuredList else ProjectsStyles.earlierGrid) }) {
        projects.forEach { project ->
            key(project.slug) {
                val reveal = rememberReveal()
                Li(attrs = {
                    classes(ProjectsStyles.item)
                    reveal()
                }) {
                    ProjectCard(project, featured, onNavigate)
                }
            }
        }
    }
}

/**
 * One project, from the Figma ProjectCard: icon, meta line and badge, name, description, skills and link pills (stores,
 * live website, source code, privacy policy). A
 * [featured] card uses the large layout on wide screens and the compact one on phones.
 */
@Composable
private fun ProjectCard(project: ProjectUi, featured: Boolean, onNavigate: (Route) -> Unit) {
    val strings = LocalStrings.current
    Card(glass = true, attrs = {
        classes(ProjectsStyles.card)
        if (featured) classes(ProjectsStyles.cardFeatured)
    }) {
        Img(src = project.iconUrl, alt = project.name, attrs = {
            classes(ProjectsStyles.icon)
            attr("width", ICON_SIZE_PX)
            attr("height", ICON_SIZE_PX)
            attr("loading", "lazy")
        })
        Div(attrs = { classes(ProjectsStyles.head) }) {
            P(attrs = { classes(ProjectsStyles.meta) }) {
                Span { Text(project.meta) }
                if (project.comingSoon) StatusBadge(strings.comingSoon)
            }
            H3(attrs = { classes(ProjectsStyles.name) }) { Text(project.name) }
        }
        project.description?.let { description ->
            P(attrs = { classes(ProjectsStyles.description) }) { Text(description) }
        }
        SkillList(ownerId = project.slug, skills = project.skills)
        val websiteUrl = project.websiteUrl
        val sourceUrl = project.sourceUrl
        val policyRoute = project.policyRoute
        if (project.storeLinks.isNotEmpty() || websiteUrl != null || sourceUrl != null || policyRoute != null) {
            Div(attrs = { classes(ProjectsStyles.links) }) {
                project.storeLinks.forEach { LinkPill(it.store.label, it.url) { StoreIcon(it.store) } }
                websiteUrl?.let { LinkPill(strings.visitSite, it) { GlobeIcon() } }
                sourceUrl?.let { LinkPill(strings.sourceCode, it) { GitHubIcon() } }
                policyRoute?.let { LinkPill(strings.privacyPolicy, it, onNavigate) { PrivacyPolicyIcon() } }
            }
        }
    }
}

/** The store's name. Brand names are not translated. */
private val Store.label: String
    get() = when (this) {
        Store.GOOGLE_PLAY -> "Google Play"
        Store.APP_STORE -> "App Store"
    }

@Composable
private fun StoreIcon(store: Store) = when (store) {
    Store.GOOGLE_PLAY -> GooglePlayIcon()
    Store.APP_STORE -> AppStoreIcon()
}
